import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styles from '../less/BaseLayout.module.less'
import { LoadingOutlined, LockOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Layout,
  message,
  Row,
  theme,
  Typography,
} from 'antd'
import { gutter } from '../lib/layout'
import { useLogo } from '../lib/themeController'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { useAuth } from 'reactfire'
import { errorResponse } from '../lib/errorResponse'
import { validatePasswordRule } from '../lib/validatePasswordRule'

export default function Action() {
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [passwordResetLoading, setPasswordResetLoading] = React.useState(false)

  const auth = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const redirectTimerRef = useRef(null)
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const logoBlack = useLogo('bw')

  useEffect(() => {
    async function init() {
      try {
        if (searchParams.has('oobCode') && searchParams.has('mode')) {
          const oobCode = searchParams.get('oobCode')
          const mode = searchParams.get('mode')
          switch (mode) {
            case 'verifyEmail':
            case 'verifyAndChangeEmail':
              await applyActionCode(auth, oobCode)
              if (searchParams.has('continueUrl')) {
                window.location.replace(searchParams.get('continueUrl'))
              } else {
                window.location.replace('/#emailVerified')
              }
              break
            case 'resetPassword':
              await verifyPasswordResetCode(auth, oobCode)
              break
            case 'recoverEmail':
              await applyActionCode(auth, oobCode)
              window.location.replace('/login#emailRecovered')
              break
            case 'revertSecondFactorAddition':
              await applyActionCode(auth, oobCode)
              window.location.replace('/login#mfaReverted')
              break
            default:
              errorResponse(new Error(`Unknown action (${mode}).`))
              break
          }
        } else {
          // /action is only entered via Firebase-generated email-action
          // links, which always include `mode` and `oobCode`. Landing here
          // bare is a navigation mistake (stale bookmark, history
          // autocomplete, manual URL), not a software bug — surface a
          // friendly message and redirect home rather than firing a Sentry
          // event and a generic "Ups!" toast.
          setError(t('frontend:auth.linkInvalidOrExpired'))
          redirectTimerRef.current = setTimeout(() => {
            navigate('/', { replace: true })
          }, 3000)
        }
      } catch (error) {
        let message = error.message
        if (error.code) {
          switch (error.code) {
            case 'auth/expired-action-code':
              message = t('frontend:auth.linkExpired')
              break
            case 'auth/invalid-action-code':
              message = t('frontend:auth.linkInvalid')
              break
            case 'auth/user-disabled':
              message = t('frontend:auth.userDisabled')
              break
            case 'auth/user-not-found':
              message = t('frontend:auth.userNotFound')
              break
            // Some email clients (notably Outlook desktop) and privacy tools
            // strip the Referer header on outgoing clicks, which Firebase's
            // referrer-restricted API key rejects. Surfaces as either a
            // specific referer-null code or a generic network failure.
            case 'auth/requests-from-referer-null-are-blocked':
            case 'auth/network-request-failed':
              message = t('frontend:auth.linkOpenFailed')
              errorResponse(error)
              break
            default:
              errorResponse(error)
              break
          }
        } else {
          errorResponse(error)
        }
        setError(message)
      } finally {
        setLoading(false)
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    init()
    // Run once on mount: /action is a terminal page that performs a single
    // OOB action and then either redirects away or stays on an error screen.
    // Without this, every state change re-runs init(), causing duplicate
    // toasts and Sentry events on the error path.
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function savePasswordReset(values) {
    try {
      setPasswordResetLoading(true)
      await confirmPasswordReset(
        auth,
        searchParams.get('oobCode'),
        values.password
      )
      navigate('/', { replace: true })
      message.success(t('frontend:auth.passwordResetSuccess'))
    } catch (error) {
      errorResponse(error)
    } finally {
      setPasswordResetLoading(false)
    }
  }

  let component
  if (!error) {
    switch (searchParams.get('mode')) {
      case 'verifyEmail':
      case 'verifyAndChangeEmail':
        component = (
          <>
            <Typography.Title level={3}>
              {t('frontend:auth.verifyingEmailTitle')}
            </Typography.Title>
            <Typography.Paragraph>
              {t('frontend:auth.verifyingEmailText')}
            </Typography.Paragraph>
          </>
        )
        break
      case 'recoverEmail':
        component = (
          <>
            <Typography.Title level={3}>
              {t('frontend:auth.recoveringEmailTitle')}
            </Typography.Title>
            <Typography.Paragraph>
              {t('frontend:auth.recoveringEmailText')}
            </Typography.Paragraph>
          </>
        )
        break
      case 'revertSecondFactorAddition':
        component = (
          <>
            <Typography.Title level={3}>
              {t('frontend:auth.removing2faTitle')}
            </Typography.Title>
            <Typography.Paragraph>
              {t('frontend:auth.removing2faText')}
            </Typography.Paragraph>
          </>
        )
        break
      case 'resetPassword':
        component = (
          <>
            <Typography.Title level={3}>
              {t('frontend:auth.resetPasswordTitle')}
            </Typography.Title>
            {!loading && (
              <>
                <Typography.Paragraph>
                  {t('frontend:auth.resetPasswordText')}
                </Typography.Paragraph>
                <Form
                  colon={false}
                  layout="vertical"
                  form={form}
                  onFinish={savePasswordReset}
                >
                  <Form.Item
                    name={['password']}
                    validateDebounce={1000}
                    rules={[
                      {
                        required: true,
                        message: t('frontend:auth.newPasswordRequired'),
                      },
                      validatePasswordRule(auth),
                    ]}
                    style={{ marginBottom: '10px' }}
                  >
                    <Input
                      prefix={
                        <LockOutlined
                          style={{
                            color: token.colorTextQuaternary,
                          }}
                        />
                      }
                      type="password"
                      placeholder={t('frontend:auth.newPassword')}
                    />
                  </Form.Item>
                  <Form.Item
                    name={['confirmPassword']}
                    validateDebounce={1000}
                    rules={[
                      {
                        required: true,
                        message: t('frontend:auth.confirmPasswordRequired'),
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve()
                          }
                          return Promise.reject(
                            new Error(t('frontend:auth.confirmationMismatch'))
                          )
                        },
                      }),
                    ]}
                  >
                    <Input
                      prefix={
                        <LockOutlined
                          style={{ color: token.colorTextQuaternary }}
                        />
                      }
                      type="password"
                      placeholder={t('frontend:auth.confirmNewPassword')}
                    />
                  </Form.Item>
                  <Button
                    style={{ marginTop: '10px' }}
                    htmlType="submit"
                    loading={passwordResetLoading}
                    block
                    type="primary"
                  >
                    {t('frontend:auth.setPassword')}
                  </Button>
                </Form>
              </>
            )}
          </>
        )
        break
      default:
    }
  }

  return (
    <Layout className={styles.baseLayout}>
      <Layout style={{ margin: '50px 8px' }}>
        <Row gutter={gutter} type="flex" justify="center" align="middle">
          <Col align="center" xs={24} style={{ maxWidth: '450px' }}>
            <img
              src={logoBlack}
              alt="cleverlohn"
              className={styles.logo}
              style={{ marginBottom: '25px' }}
            />
            <Card bordered={false}>
              {component}
              {loading && (
                <>
                  <div style={{ margin: '20px 0' }}>
                    <LoadingOutlined
                      style={{
                        fontSize: '40px',
                        color: token.colorTextTertiary,
                      }}
                    />
                  </div>
                  <Typography.Text type="secondary">
                    {t('frontend:common.pleaseWait')}
                  </Typography.Text>
                </>
              )}
              {error && (
                <>
                  <Typography.Title level={3}>
                    {t('frontend:auth.errorOccurred')}
                  </Typography.Title>
                  <Typography.Paragraph>
                    <Typography.Text
                      style={{ fontSize: '16px', wordBreak: 'break-all' }}
                      strong
                      copyable
                    >
                      {error}
                    </Typography.Text>
                  </Typography.Paragraph>
                  <Typography.Paragraph>
                    <Typography.Text type="secondary">
                      {t('frontend:auth.errorHelp')}
                    </Typography.Text>
                  </Typography.Paragraph>
                  <Link to="/">
                    <Button block type="primary">
                      {t('frontend:common.back')}
                    </Button>
                  </Link>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Layout>
    </Layout>
  )
}
